// src/utils/parseTxError.js
//
// A shared utility function to parse Ethers.js and network errors
// and return a short, user-friendly message.

const MAX_MESSAGE_LENGTH = 120;

// Known revert strings from Prospect2WorkerLicenseV7, mapped to operator-facing
// guidance. These are matched against the whole error payload (not just the
// text after "execution reverted:") because ethers wraps estimateGas failures
// in a multi-kilobyte blob that merely contains the reason somewhere inside.
//
// Every `message` below MUST stay well under MAX_MESSAGE_LENGTH (target <= 110
// chars): callers such as NFTDetailModal store the parsed string in state and
// then re-parse it (`parseTxError({ message: previousOutput })`), so anything
// longer than MAX_MESSAGE_LENGTH would collapse into the generic fallback on
// the second pass.
const KNOWN_REVERTS = [
  {
    test: /would exceed max supply|max supply reached/i,
    message:
      'Collection cap reached. Raise maxSupply via setMaxSupply from the owner wallet, then mint again.'
  },
  {
    test: /id already minted/i,
    message:
      'That token id already exists on-chain. Refresh the minted list to pick fresh ids and try again.'
  },
  {
    test: /batch too big/i,
    message:
      'Too many ids in one transaction. Reduce the batch to at most MAX_MINT_ID_BATCH and try again.'
  },
  {
    test: /new max supply less than minted/i,
    message:
      'New max supply is below the number already minted. Choose a value at or above totalSupply.'
  },
  {
    test: /not on allow-list/i,
    message: 'This wallet is not on the allow-list for this mint.'
  },
  {
    test: /you already own an nft/i,
    message: 'You already own an NFT.'
  },
  {
    test: /invalid signature/i,
    message:
      'Mint signature invalid. Reconnect the wallet, refresh the mint modal, and try again.'
  }
];

// Outputs this module has already produced. Re-parsing one of them must be a
// no-op — see the NFTDetailModal double-parse noted above.
const PARSED_MESSAGES = new Set(KNOWN_REVERTS.map(({ message }) => message));

function isAlreadyParsed(str) {
  return typeof str === 'string' && PARSED_MESSAGES.has(str.trim());
}

/**
 * Collapses any raw string pulled out of an error into something short enough
 * to render. Every path that returns unvetted text must go through this —
 * revert payloads straight off the RPC routinely run to thousands of chars.
 */
function clean(str) {
  const trimmed = (str || '').trim();
  if (!trimmed) return 'Transaction reverted.';
  if (trimmed.length > MAX_MESSAGE_LENGTH) {
    return 'Transaction failed. See console for details.';
  }
  return trimmed;
}

function matchKnownRevert(str) {
  if (!str) return null;
  const hit = KNOWN_REVERTS.find(({ test }) => test.test(str));
  return hit ? hit.message : null;
}

/**
 * Every place ethers may stash a revert reason, flattened for matching.
 */
function errorTextCandidates(error) {
  return [
    error.reason,
    error.message,
    error.error && error.error.message,
    error.error && error.error.data && error.error.data.message,
    error.data && error.data.message
  ].filter((s) => typeof s === 'string' && s.length > 0);
}

export function parseTxError(error) {
  if (!error) {
    return 'An unknown error occurred.';
  }

  // Idempotency: a message this module already produced passes straight
  // through, so double-parsing never re-matches or truncates it.
  for (const candidate of errorTextCandidates(error)) {
    if (isAlreadyParsed(candidate)) return candidate.trim();
  }

  // Known contract reverts win over every generic path below: they carry the
  // action the operator actually has to take.
  for (const candidate of errorTextCandidates(error)) {
    const known = matchKnownRevert(candidate);
    if (known) return known;
  }

  // Common Ethers error codes:
  // - INSUFFICIENT_FUNDS
  // - UNPREDICTABLE_GAS_LIMIT
  // - CALL_EXCEPTION
  // - SERVER_ERROR, etc.

  // If there's a "code" property, we check common Ethers error codes
  if (error.code === 'INSUFFICIENT_FUNDS') {
    return 'You do not have enough ETH to cover gas fees. Please add more ETH to your wallet.';
  }

  // Even if error.code is "UNPREDICTABLE_GAS_LIMIT", let's still parse
  // the inner revert reason. If none is found, fallback to the standard message.
  if (error.code === 'UNPREDICTABLE_GAS_LIMIT') {
    // Try to parse a revert reason from error.message or error.error.message
    const maybeRevertReason = extractRevertReason(error);
    if (maybeRevertReason) {
      return maybeRevertReason;
    }
    return 'Transaction may fail due to lack of gas. Make sure you have enough ETH for gas fees.';
  }

  // If there's a nested error with a message, parse that
  if (error.error && error.error.message) {
    return shortMessageFromString(error.error.message);
  }
  // Or if there's a top-level error message
  if (error.message) {
    return shortMessageFromString(error.message);
  }

  // Fallback
  return 'Transaction failed. Please try again or check console for details.';
}

/**
 * A helper that attempts to parse the known "execution reverted" string
 * from either `error.message` or `error.error.message`.
 */
function extractRevertReason(error) {
  // We check either top-level message or nested error
  const topLevel = error.message || '';
  const nested = (error.error && error.error.message) || '';

  // If we see "execution reverted: ..." in either, parse that out
  if (topLevel.includes('execution reverted:')) {
    return shortMessageFromString(topLevel);
  }
  if (nested.includes('execution reverted:')) {
    return shortMessageFromString(nested);
  }
  return null;
}

/**
 * This helper tries to shorten typical giant revert messages
 * by removing known prefixes and special-casing known errors.
 */
function shortMessageFromString(str) {
  if (!str) return 'Transaction failed. Please try again or check console for details.';

  // Already one of our own outputs — return it verbatim.
  if (isAlreadyParsed(str)) return str.trim();

  // Special-cased reverts first, matched against the full string.
  const known = matchKnownRevert(str);
  if (known) return known;

  // If there's a known prefix like "execution reverted:", remove it.
  // The remainder is still unvetted text, so it goes through clean().
  const prefix = 'execution reverted:';
  if (str.includes(prefix)) {
    return clean(str.split(prefix)[1]);
  }

  // If we see "INSUFFICIENT_FUNDS" text, produce a short message
  if (/insufficient funds/i.test(str)) {
    return 'You do not have enough ETH to cover gas fees.';
  }

  // If we see "UNPREDICTABLE_GAS_LIMIT" text, produce a short message
  if (/UNPREDICTABLE_GAS_LIMIT/i.test(str)) {
    return 'Transaction may fail due to lack of gas.';
  }

  // Truncates when too long, otherwise returns the message as is
  return clean(str);
}
